import GuestCardPage from './GuestCardPage';
import IdVerificationPage from './verification/IdVerificationPage';
import TourConfirmationPage from './confirmation/TourConfirmationPage';
import * as React from 'react';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';
import {
    guestCardGuidCapture,
    propertySlugCapture,
    TourBasePath,
    tourGuidCapture
} from '../ProspectRouteValues';
import { useFetch } from '../../../../hooks/useFetch';
import { getPropertyByKey, Property } from '../../../../api/Property';
import { LoadingDisplay } from '../../../common/LoadingOverlay';
import { prospectPages, prospectPath } from '../ProspectRouter';
import ProspectLayout from '../../../layouts/ProspectLayout';
import CreditCardPage from './CreditCardPage';
import '../../../../styles/FaqsPage.css';
import '../../../../styles/TourConfirmedSubPage.css';
import Row from 'reactstrap/lib/Row';
import { Col } from 'reactstrap';
import RegistrationPage from '../public/RegistrationPage';
import AuthorizedRoute from '../../../common/AuthorizedRoute';
import { Roles, SourceType } from '../../../../utilities/Enums';
import MyProfilePage from '../account/MyProfilePage';
import LoginPage from '../public/LoginPage';
import SignupSuccessfulPage from './confirmation/SignupSuccessfulPage';
import NotFoundPage from './property/NotFoundPage';
import * as moment from 'moment';
import NotLiveSubPage from './property/NotLivePage';
import CancelledSubPage from './property/CancelledSubPage';
import * as queryString from 'query-string';
import { getSourceData, setSourceData, SourceData } from '../../../../utilities/sourceData';

export interface DualRoutedProps extends RouteComponentProps<{ propertySlug?: string }> {}

export interface TourSignupProps
    extends RouteComponentProps<{
        propertySlug: string;
        guestCardGuid?: string;
    }> {}

export interface TourSignedUpProps
    extends RouteComponentProps<{
        propertySlug: string;
        guestCardGuid: string;
        tourGuid: string;
    }> {}

export enum TourPage {
    Registration = 'register',
    Profile = 'profile',
    Login = 'login',
    GuestCard = 'guestcard',
    IdVerification = 'verify',
    CcQualification = 'qualify',
    Confirmation = 'confirm',
    SignupSuccess = 'success',
    TourSignUp = 'signup'
}

export const PropertyContext = React.createContext<Property | undefined>(undefined);

export const tourSignupPath =
    (propertySlug: string, guestCardGuid?: string) => (tourPage: TourPage) =>
        `${TourBasePath}/${propertySlug}/${tourPage}/${guestCardGuid || ''}`;
const tourRoutingPath = tourSignupPath(propertySlugCapture, guestCardGuidCapture);

export const tourSignedUpPath =
    (propertySlug: string, guestCardGuid: string, tourGuid: string) => (tourPage: TourPage) =>
        `${tourSignupPath(propertySlug, guestCardGuid)(tourPage)}/${tourGuid}`;
export const tourSignedUpRoutingPath = tourSignedUpPath(
    propertySlugCapture,
    guestCardGuidCapture,
    tourGuidCapture
);

const defaultImage = require('../../../../images/demo-unit.png');

const IOS_APP_STORE_URL = 'https://apps.apple.com/us/app/tour24-self-guided-apt-tour/id1462185580';

const ANDROID_PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.toursphere.tour24';

const TourRouterSwitch = (props: TourSignupProps & { property: Property }) => (
    <PropertyContext.Provider value={props.property}>
        <Row>
            <Col xs={12} lg={6}>
                <header className="tour-property-header">
                    <h4 className="property-name">{props.property.name}</h4>
                </header>

                <Switch>
                    <Route
                        path={tourRoutingPath(TourPage.Registration)}
                        component={RegistrationPage}
                    />
                    <Route
                        path={tourRoutingPath(TourPage.TourSignUp)}
                        component={RegistrationPage}
                    />

                    <Route path={tourRoutingPath(TourPage.Profile)} component={MyProfilePage} />

                    <Route path={tourRoutingPath(TourPage.Login)} component={LoginPage} />

                    <AuthorizedRoute
                        roles={[Roles.ProspectUser]}
                        path={tourRoutingPath(TourPage.GuestCard)}
                        component={GuestCardPage}
                    />

                    <AuthorizedRoute
                        roles={[Roles.ProspectUser]}
                        path={tourRoutingPath(TourPage.IdVerification)}
                        component={IdVerificationPage}
                    />
                    <AuthorizedRoute
                        roles={[Roles.ProspectUser]}
                        path={tourRoutingPath(TourPage.CcQualification)}
                        component={CreditCardPage}
                    />
                    <AuthorizedRoute
                        roles={[Roles.ProspectUser]}
                        path={tourRoutingPath(TourPage.Confirmation)}
                        component={TourConfirmationPage}
                    />

                    <AuthorizedRoute
                        roles={[Roles.ProspectUser]}
                        path={tourSignedUpRoutingPath(TourPage.SignupSuccess)}
                        component={SignupSuccessfulPage}
                    />

                    <Redirect
                        path={`${TourBasePath}/${propertySlugCapture}`}
                        to={{ pathname: prospectPath(prospectPages.tours) }}
                    />
                </Switch>
            </Col>
            <Col className="d-none d-lg-block" lg={6}>
                <img
                    src={props.property.propertyImage || defaultImage}
                    className="tour-workflow-property-image"
                />
            </Col>
        </Row>
    </PropertyContext.Provider>
);

const TourRouter = (props: TourSignupProps) => {
    React.useEffect(() => {
        const queryData = queryString.parse(props.location.search) as SourceData;
        var existingData = getSourceData();
        const combinedSourceData = {
            ...existingData,
            ...queryData
        };
        setSourceData(combinedSourceData);
    }, []);

    let { data: property, loading } = useFetch(
        () => getPropertyByKey(props.match.params.propertySlug),
        [],
        undefined
    );

    React.useEffect(() => {
        const fetchMobileAppURL = async () => {
            try {
                const location = getEndParam(props.location.pathname);
                if (property && property.slug && location !== TourPage.GuestCard) {
                    if (property && property.isMobileRedirect) {
                        var existingData = getSourceData();
                        var userAgent = navigator.userAgent;
                        let mobileAppURL = '';
                        if (/iPhone|iPad|iPod/i.test(userAgent)) {
                            // Redirect to App Store with referral code
                            mobileAppURL = IOS_APP_STORE_URL;
                        } else if (/Android/i.test(userAgent)) {
                            // Redirect to Play Store with referral code
                            mobileAppURL = ANDROID_PLAY_STORE_URL;
                        }

                        if (mobileAppURL) {
                            var existingData = getSourceData();
                            var app = {
                                launchApp: (
                                    pSlug: string,
                                    source?: string,
                                    sourceType?: SourceType
                                ) => {
                                    const murl = `t24://tour/register?slug=${pSlug}&source=${source}&sourceType=${sourceType}`;
                                    window.location.replace(murl);
                                    setTimeout(app.openWebApp, 1000);
                                },
                                openWebApp: () => {
                                    console.log('openWebApp --->?');
                                    window.location.replace(mobileAppURL);
                                }
                            };
                            app.launchApp(
                                property.slug,
                                existingData.source,
                                existingData.sourceType
                            );
                        }
                    }
                }
            } catch (error) {
                console.log('Error fetching mobile app URL', error);
            }
        };
        fetchMobileAppURL();
    }, [property]);

    const displayContent = () => {
        if (loading) {
            return <LoadingDisplay />;
        }

        if (!property) {
            return <NotFoundPage />;
        }

        let now = moment.utc();
        if (moment.utc(property.cancellationDate).isSameOrBefore(now)) {
            return <CancelledSubPage propertyInfo={property} />;
        }

        if (moment.utc(property.goLiveDate).isAfter(now)) {
            return <NotLiveSubPage propertyInfo={property} />;
        }

        return <TourRouterSwitch {...props} property={property} />;
    };

    const getEndParam = (url: string) => {
        if (url) {
            const endParam = url.split('/').filter(Boolean).pop();
            return endParam;
        }
        return null;
    };

    return (
        <ProspectLayout
            faqsSlug={
                (property && property.faqsAreActive && props.match.params.propertySlug) || undefined
            }
        >
            {displayContent()}
        </ProspectLayout>
    );
};

export default TourRouter;



// WEBPACK FOOTER //
// ./src/components/pages/prospect/signup/TourRouter.tsx